// @ts-nocheck
// app/catalog.tsx — Editorial catalog wrapper
// Wraps the existing CoursesCatalog component in the editorial chrome.

import { useCallback, useMemo, useState } from 'react';
import Head from 'expo-router/head';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft, MessageCircle } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import { CoursesCatalog, type SchemaCourseLite } from '@/components/CoursesCatalog';
import { EditorialButton, EditorialFooter, Eyebrow, Headline, Paper, PageHero, Section } from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SANS = theme.fonts.regular;
const IS_WEB = Platform.OS === 'web';

const normalizeStr = (v?: string | null): string => (v ?? '').toString().trim();
const getFirstUrl = (raw?: string | null): string | null => {
  const s = normalizeStr(raw); if (!s) return null;
  return s.split(',').map(p => p.trim()).filter(Boolean)[0] ?? null;
};
const normalizeUrl = (raw?: string | null): string | null => {
  const s = normalizeStr(raw); if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[\w-]+\.[\w.-]+/i.test(s)) return `https://${s}`;
  return s;
};

export default function CatalogPage() {
  const router = useRouter();
  const params = useLocalSearchParams<{ topic?: string }>();
  const initialTopic = params.topic ? decodeURIComponent(params.topic as string) : undefined;

  const [schemaCourses, setSchemaCourses] = useState<SchemaCourseLite[]>([]);

  const itemListJsonLd = useMemo(() => {
    if (!IS_WEB) return null;
    const list = (schemaCourses || []).slice(0, 50).map((c, idx) => {
      const url = normalizeUrl(getFirstUrl(c.source_urls)) || 'https://kursiaii.com/catalog';
      return {
        '@type': 'ListItem', position: idx + 1,
        item: {
          '@type': 'Course',
          name: normalizeStr(c.title) || 'קורס',
          url,
          ...(normalizeStr(c.provider) ? { provider: { '@type': 'Organization', name: c.provider } } : {}),
        },
      };
    });
    if (list.length === 0) return null;
    return {
      '@context': 'https://schema.org', '@type': 'ItemList',
      name: 'קטלוג הקורסים של Kursi',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: list.length, itemListElement: list,
    };
  }, [schemaCourses]);

  const handleSchemaCourses = useCallback((courses: SchemaCourseLite[]) => {
    setSchemaCourses(courses || []);
  }, []);

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>הקטלוג · Kursi · מעל 1,200 קורסים בישראל</title>
          <meta name="description" content="קטלוג מלא של מעל 1,200 קורסים אונליין ופרונטליים בישראל. תכנות · שיווק דיגיטלי · AI · פסיכומטרי · שוק ההון · סייבר." />
          <link rel="canonical" href="https://kursiaii.com/catalog" />
          {itemListJsonLd && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />
          )}
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER, paddingBottom: 24 }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1240}>
          <PageHero
            kicker="גיליון הקטלוג"
            title={<>1,200+ קורסים.{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>מסונן בשבילכם.</Text></>}
            lede="כל הקורסים, סינון לפי תחום, רמה ומחיר. אם זה הרבה מדי — תשאלו את ה-AI שלנו ויחזיר לכם שלושה."
            meta={initialTopic ? `מסונן לפי: ${initialTopic}` : 'כל התחומים · עודכן היום'}
          />

          {/* Chat bridge bar */}
          <View style={s.bridge}>
            <View style={{ flex: 1, paddingRight: 0 }}>
              <Text style={s.bridgeKicker}>הצעת עורך</Text>
              <View style={{ height: 6 }} />
              <Text style={s.bridgeTitle}>לא בטוחים מה מתאים לכם?</Text>
              <View style={{ height: 6 }} />
              <Text style={s.bridgeBody}>כתבו מטרה קצרה — נחזיר שלוש התאמות מדויקות מבלי שתצטרכו לעבור על כל הקטלוג.</Text>
            </View>
            <EditorialButton
              label="שאלו את ה-AI"
              variant="primary" size="md"
              icon={<MessageCircle size={14} color={PAPER} />}
              onPress={() => router.push({ pathname: '/chat', params: { initialMessage: 'אני צריך עזרה לבחור קורס מתאים', skipWelcome: 'true', source: 'catalog' } })}
            />
          </View>
        </Section>

        <Section py="md" px="md" max={1240}>
          <CoursesCatalog onSchemaCoursesChange={handleSchemaCourses} initialTopic={initialTopic} />
        </Section>

        <EditorialFooter
          columns={[
            { title: 'מקטעים', items: [
              { label: 'תחומי לימוד', href: '/fields' },
              { label: 'שאלון התאמה', href: '/quiz' },
              { label: 'מדריכי בחירה', href: '/guides' },
            ]},
            { title: 'המוצר', items: [
              { label: 'איך זה עובד', href: '/how-it-works' },
              { label: 'שאלות נפוצות', href: '/faq' },
              { label: 'אודות', href: '/about' },
            ]},
          ]}
          onNavigate={(href) => router.push(href as any)}
        />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  bridge: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 18,
    borderWidth: 1, borderColor: INK, backgroundColor: PAPER_SOFT,
    padding: 22,
    flexWrap: 'wrap',
  } as any,
  bridgeKicker: { fontFamily: MONO, fontSize: 10, color: ACCENT, letterSpacing: 1.8, textTransform: 'uppercase' as any, textAlign: 'right' },
  bridgeTitle: { fontFamily: SERIF_BLACK, fontSize: 26, color: INK, letterSpacing: -0.5, textAlign: 'right' },
  bridgeBody: { fontFamily: SANS, fontSize: 14, color: INK_SOFT, lineHeight: 22, textAlign: 'right' },
});
